import cloneDeep from 'lodash.clonedeep';

import { removePrivateKeysFromObj } from '@/lib/compare';
import { getPreviousDate } from '@/lib/date';
import { Filter } from '@/lib/filter';
import { FilterSet } from '@/lib/filter-set';
import { FilterSetCollection } from '@/lib/filter-set-collection';
import { UserFilterSet } from '@/lib/user-filter-set';
import router from '@/router/index';
import AutocompleteService from '@/services/autocomplete';
import FiltersService from '@/services/filters';

// Filters store module
//
// State is namespaced PER MODULE (`safety`, `formulary`, ...) rather than held as one global filter
// set. Each module owns an independent slice with the same shape — its catalog, the user's saved
// sets, and the loaded flags — so two modules' filters can never leak into each other's bars.
//
// Every getter is a FUNCTION of the module name, since Vuex getters can't take arguments directly:
//
//   ...mapGetters(['filters'])   then   this.filters(SAFETY_MODULE)
//
// and every action/mutation payload carries a `module`. The module name is the only addition to the
// existing API — the values behind it are the same FilterSet / FilterSetCollection instances as
// before, so `lib/filter-set.js` and friends are untouched and stay reusable by any module.
export const SAFETY_MODULE = 'safety';
export const FORMULARY_MODULE = 'formulary';

// `/api/filters/` returns ONE catalog for every module the user can see, so a module's slice has to
// be filtered out of that shared response. Each module declares which capabilities mark a filter as
// its own; a filter belongs to a module if it declares at least one of them.
//
// This is a partition, not a permission check — `FilterSet.filtersCapableOf` still decides what the
// USER can see, and it runs on top of this. The lists exist because "which module owns this filter"
// is not derivable from the capability alone: Safety's filters span `infusions`, employee-only and
// per-dashboard capabilities, and a third module would add its own.
//
// Keep these in step with the `capabilities` in
// svc/bhapi/k8s/values/models/filters/models.yaml — a capability missing here means its filters load
// into no module and silently disappear from every bar.
export const MODULE_FILTER_CAPABILITIES = {
  [SAFETY_MODULE]: [
    'infusions',
    'bainbridge_employee',
    'filters::facility',
    'nursing',
    'analytics::medfusion',
    'dashboards::medications-beta',
    'dashboards::network-demo',
  ],
  [FORMULARY_MODULE]: ['formulary'],
};

// The filters in `definitions` belonging to `module`. An unknown module owns nothing rather than
// everything, so a typo empties one bar instead of cross-loading two.
const filtersForModule = (definitions, module) => {
  const owned = MODULE_FILTER_CAPABILITIES[module] || [];
  return definitions.filter((definition) =>
    (definition.capabilities || []).some((capability) => owned.includes(capability))
  );
};

// The per-module slice. Kept as a factory so each module gets its own instances rather than
// sharing one FilterSet by reference.
const moduleState = () => ({
  defaultUserFilterSet: new UserFilterSet(),
  filters: new FilterSet(),
  filterSetId: null,
  filtersLoaded: false,
  userFilterSets: new FilterSetCollection(),
  userFilterSetsLoaded: false,
});

// Turn the filter DEFINITIONS returned by `/api/filters/` into a FilterSet of Filter instances.
// Definitions use the API's snake_case shape; `Filter.setProps` camelCases them.
const buildFilterSet = (definitions) => {
  const filterSet = new FilterSet();
  definitions.forEach((definition) => {
    const filter = new Filter();
    if (definition.default_value) {
      // If a date filter has a default value that is based on local time, determine default value
      if (definition.default_value.localTime) {
        definition.default_value.primaryDateField = getPreviousDate(definition.default_value.primaryDateField);
        definition.default_value.secondaryDateField = getPreviousDate(definition.default_value.secondaryDateField);
        delete definition.default_value.localTime;
      }
    }
    filter.setProps(definition);
    filterSet[filter.id] = filter;
  });
  return filterSet;
};

// Read a module's slice, creating it on demand. Vuex 4 state is a reactive Proxy, so assigning a
// new key here is picked up without Vue.set.
const sliceFor = (state, module) => {
  if (!state.modules[module]) {
    state.modules[module] = moduleState();
  }
  return state.modules[module];
};

export default {
  state: {
    modules: {
      [SAFETY_MODULE]: moduleState(),
      [FORMULARY_MODULE]: moduleState(),
    },
  },
  mutations: {
    RESET_FILTERS(state, module) {
      // No module means log-out: clear every slice, so one user's filters never survive into the
      // next session.
      const targets = module ? [module] : Object.keys(state.modules);
      targets.forEach((target) => {
        state.modules[target] = moduleState();
      });
    },
    SET_DEFAULT_USER_FILTER_SET(state, { module, defaultUserFilterSet }) {
      sliceFor(state, module).defaultUserFilterSet = defaultUserFilterSet;
    },
    SET_FILTERS(state, { module, filters }) {
      sliceFor(state, module).filters = filters;
    },
    SET_FILTER_SET_ID(state, { module, filterSetId }) {
      sliceFor(state, module).filterSetId = filterSetId;
    },
    SET_FILTERS_LOADED(state, { module, filtersLoaded }) {
      sliceFor(state, module).filtersLoaded = filtersLoaded;
    },
    SET_FILTER_VALUE(state, { module, id, key, value }) {
      sliceFor(state, module).filters[id][key] = value;
    },
    SET_USER_FILTER_SETS(state, { module, userFilterSets }) {
      sliceFor(state, module).userFilterSets = userFilterSets;
    },
    SET_USER_FILTER_SETS_LOADED(state, { module, userFilterSetsLoaded }) {
      sliceFor(state, module).userFilterSetsLoaded = userFilterSetsLoaded;
    },
  },
  actions: {
    refreshFacetedFilters({ dispatch, state, getters }, { module, filterIds: filterIdArr }) {
      const slice = sliceFor(state, module);
      const uniqueFacetIds = [];
      filterIdArr.forEach((filterId) => {
        slice.filters.facetedFilterIds(filterId).forEach((facetId) => {
          if (!uniqueFacetIds.includes(facetId)) {
            uniqueFacetIds.push(facetId);
          }
        });
      });
      uniqueFacetIds.forEach((facetId) => {
        dispatch('setFilterValue', { module, id: facetId, key: 'autocompleteLoading', value: true });
        const filterValues = new FilterSet();
        const filterIds = [...getters.filterDefaults(module).filterIds, ...getters.filterSet(module).filterIds];
        filterIds.forEach((f) => {
          if (getters.filterSet(module)[f]) {
            filterValues[f] = getters.filterSet(module)[f];
          } else {
            filterValues[f] = getters.filterDefaults(module)[f];
          }
        });
        const subject =
          slice.filters[facetId].dashboardAutocompleteSubject || slice.filters[facetId].autocompleteSubject;
        if (subject) {
          const filterQuery = slice.filters.autocompleteQuery(facetId, filterValues);
          AutocompleteService.getAutocompleteResults(subject, filterQuery).then((results) => {
            const hasMappedValues = results.some((result) => typeof result === 'object');
            if (hasMappedValues) {
              const possibleValues = results.map((result) => Object.values(result)[0]);
              dispatch('setFilterValue', { module, id: facetId, key: 'possibleValues', value: possibleValues });
            } else {
              dispatch('setFilterValue', { module, id: facetId, key: 'possibleValues', value: results });
            }
            dispatch('setFilterValue', { module, id: facetId, key: 'autocompleteLoading', value: false });
          });
        }
      });
    },
    // Omit `module` to clear every module's filters (log-out).
    resetFilters({ commit }, module) {
      commit('RESET_FILTERS', module);
    },
    setDefaultUserFilterSet({ commit }, { module, defaultUserFilterSet }) {
      commit('SET_DEFAULT_USER_FILTER_SET', { module, defaultUserFilterSet });
    },
    setFilterAutocomplete({ dispatch, state, getters }, { module, filterId }) {
      const slice = sliceFor(state, module);
      const filter = slice.filters[filterId];
      if (filter && (filter.autocompleteSubject || filter.dashboardAutocompleteSubject)) {
        dispatch('setFilterValue', { module, id: filterId, key: 'autocompleteLoading', value: true });
        const subject = filter.dashboardAutocompleteSubject || filter.autocompleteSubject;
        const filterValues = new FilterSet();
        const filterIds = [...getters.filterDefaults(module).filterIds, ...getters.filterSet(module).filterIds];
        filterIds.forEach((f) => {
          if (getters.filterSet(module)[f]) {
            filterValues[f] = getters.filterSet(module)[f];
          } else {
            filterValues[f] = getters.filterDefaults(module)[f];
          }
        });
        const filterQuery = slice.filters.autocompleteQuery(filterId, filterValues);
        AutocompleteService.getAutocompleteResults(subject, filterQuery).then((results) => {
          const hasMappedValues = results.some((result) => typeof result === 'object');
          if (hasMappedValues) {
            dispatch('setFilterValue', { module, id: filterId, key: 'mappedValues', value: results });
            const possibleValues = results.map((result) => Object.values(result)[0]);
            dispatch('setFilterValue', { module, id: filterId, key: 'possibleValues', value: possibleValues });
          } else {
            dispatch('setFilterValue', { module, id: filterId, key: 'possibleValues', value: results });
          }
          dispatch('setFilterValue', { module, id: filterId, key: 'autocompleteLoading', value: false });
        });
      }
    },
    // `catalog` is the already-fetched `/api/filters/` response. It is passed in because ONE
    // catalog serves every module, so the caller loading several modules fetches once and hands the
    // same response to each; only a caller without one falls back to fetching here.
    setFilters({ dispatch, commit, state, getters }, { module, initialLoad, catalog }) {
      const slice = sliceFor(state, module);
      return new Promise((resolve, reject) => {
        if (!initialLoad) {
          let filtersChanged = false;
          const refreshList = [];
          slice.filters.filterIds.forEach((filterId) => {
            const filter = slice.filters[filterId];
            if (!filter.isOption) {
              dispatch('setFilterValue', { module, id: filterId, key: 'mappedValues', value: null });
              dispatch('setFilterValue', { module, id: filterId, key: 'possibleValues', value: null });
            }
            if (slice.filterSetId) {
              const filterValueSet = filterId in getters.filterSet(module);
              const unsetValue = filterValueSet && filter.tenantSpecific;
              if (unsetValue) {
                dispatch('setFilterValue', { module, id: filterId, key: 'value', value: null });
                refreshList.push(filterId);
                filtersChanged = true;
              }
            }
          });
          dispatch('refreshFacetedFilters', { module, filterIds: refreshList });
          if (filtersChanged && getters.filterSet(module) && !getters.filterSet(module).isEmpty) {
            getters
              .filterSet(module)
              .saveFilterSet()
              .then((response) => {
                dispatch('setFilterSetId', { module, filterSetId: response.success });
                dispatch('setFiltersLoaded', { module, filtersLoaded: true });
                resolve();
              });
          } else if (filtersChanged) {
            dispatch('setFilterSetId', { module, filterSetId: null });
            dispatch('setFiltersLoaded', { module, filtersLoaded: true });
            resolve();
          } else {
            dispatch('setFiltersLoaded', { module, filtersLoaded: true });
            resolve();
          }
        } else {
          Promise.resolve(catalog || FiltersService.getFilters(true))
            .then((filters) => {
              commit('SET_FILTERS', { module, filters: new FilterSet() });
              commit('SET_FILTERS', { module, filters: buildFilterSet(filtersForModule(filters, module)) });
              dispatch('setFiltersLoaded', { module, filtersLoaded: true });
              resolve();
            })
            .catch(() => {
              reject();
            });
        }
      });
    },
    setFiltersLoaded({ commit }, { module, filtersLoaded }) {
      commit('SET_FILTERS_LOADED', { module, filtersLoaded });
    },
    setFilterSetId({ commit }, { module, filterSetId }) {
      commit('SET_FILTER_SET_ID', { module, filterSetId });
    },
    setFilterValue({ commit }, filterData) {
      commit('SET_FILTER_VALUE', filterData);
    },
    setUserFilterSets({ dispatch, commit, state, rootGetters, getters }, { module, initialLoad, sharedSets }) {
      const slice = sliceFor(state, module);
      return new Promise((resolve, reject) => {
        if (!initialLoad) {
          if (!slice.defaultUserFilterSet.isEmpty && !router.currentRoute.value.query.filter_set) {
            if (
              slice.defaultUserFilterSet.filters.filterIds.every((f) => getters.filters(module).filterIds.includes(f))
            ) {
              // Apply only what the set names, as in the initial-load branch below.
              const refreshList = [];
              slice.defaultUserFilterSet.filters.filterIds.forEach((filterId) => {
                const filterValue = cloneDeep(slice.defaultUserFilterSet.filters[filterId].value);
                const changed = slice.filters[filterId].value !== removePrivateKeysFromObj(filterValue);
                if (changed) {
                  dispatch('setFilterValue', {
                    module,
                    id: filterId,
                    key: 'value',
                    value: removePrivateKeysFromObj(filterValue),
                  });
                  refreshList.push(filterId);
                }
              });
              dispatch('refreshFacetedFilters', { module, filterIds: refreshList });
              dispatch('setFilterSetId', { module, filterSetId: slice.defaultUserFilterSet.filterSetId });
            }
          }
          dispatch('setUserFilterSetsLoaded', { module, userFilterSetsLoaded: true });
          resolve();
        } else {
          Promise.resolve(sharedSets || FiltersService.getUserFilterSets(rootGetters.user.id, true))
            .then((sets) => {
              const userFilterSets = {};
              sets.forEach((s) => {
                if (Object.keys(s.data.filters).every((f) => getters.cumulativeFilters(module).filterIds.includes(f))) {
                  userFilterSets[s.id] = new UserFilterSet(
                    s.created,
                    s.data.id,
                    new FilterSet(),
                    s.id,
                    s.is_default,
                    s.name,
                    rootGetters.user.id
                  );
                  Object.keys(s.data.filters).forEach((filterId) => {
                    const filter = s.data.filters[filterId];
                    const filterDef = filterId in getters.cumulativeFilters(module);
                    if (filterDef) {
                      userFilterSets[s.id].filters[filterId] = new Filter();
                      userFilterSets[s.id].filters[filterId].setProps(getters.cumulativeFilters(module)[filterId]);
                      userFilterSets[s.id].filters[filterId].value = removePrivateKeysFromObj(filter);
                    }
                  });
                  if (s.is_default) {
                    dispatch('setDefaultUserFilterSet', { module, defaultUserFilterSet: userFilterSets[s.id] });
                    if (
                      !router.currentRoute.value.query.filter_set &&
                      userFilterSets[s.id].filters.filterIds.every((f) => getters.filters(module).filterIds.includes(f))
                    ) {
                      // Only the filters the set names. Nulling everything absent from it wiped
                      // selections made while this request was in flight; a default set means
                      // "start with these", and catalog defaults already cover the rest.
                      const refreshList = [];
                      userFilterSets[s.id].filters.filterIds.forEach((filterId) => {
                        const filterValue = cloneDeep(userFilterSets[s.id].filters[filterId].value);
                        const changed = slice.filters[filterId].value !== removePrivateKeysFromObj(filterValue);
                        if (changed) {
                          dispatch('setFilterValue', {
                            module,
                            id: filterId,
                            key: 'value',
                            value: removePrivateKeysFromObj(filterValue),
                          });
                          refreshList.push(filterId);
                        }
                      });
                      dispatch('refreshFacetedFilters', { module, filterIds: refreshList });
                      dispatch('setFilterSetId', { module, filterSetId: s.data.id });
                    }
                  }
                }
              });
              if (sets.every((set) => !set.is_default)) {
                dispatch('setDefaultUserFilterSet', { module, defaultUserFilterSet: new UserFilterSet() });
              }
              commit('SET_USER_FILTER_SETS', { module, userFilterSets: new FilterSetCollection(userFilterSets) });
              dispatch('setUserFilterSetsLoaded', { module, userFilterSetsLoaded: true });
              resolve();
            })
            .catch(() => {
              reject();
            });
        }
      });
    },
    setUserFilterSetsLoaded({ commit }, { module, userFilterSetsLoaded }) {
      commit('SET_USER_FILTER_SETS_LOADED', { module, userFilterSetsLoaded });
    },
  },
  getters: {
    // Each getter takes the module name. An unknown module reads as an empty slice rather than
    // throwing, so a bar can render before its module's filters have been initialized.
    filterModule: (state) => (module) => state.modules[module] || moduleState(),

    cumulativeFilters: (state, getters) => (module) => getters.filterModule(module).filters,
    defaultUserFilterSet: (state, getters) => (module) => getters.filterModule(module).defaultUserFilterSet,
    filterDefaults: (state, getters) => (module) =>
      getters.filterModule(module).filters.filtersCapableOf.filtersWithDefaults,
    filters: (state, getters) => (module) => getters.filterModule(module).filters.filtersCapableOf,
    filterSet: (state, getters) => (module) => getters.filterModule(module).filters.filtersCapableOf.filtersWithValues,
    filterSetId: (state, getters) => (module) => getters.filterModule(module).filterSetId,
    filtersLoaded: (state, getters) => (module) => getters.filterModule(module).filtersLoaded,
    userFilterSets: (state, getters) => (module) => getters.filterModule(module).userFilterSets.filterSetsCapableOf,
    userFilterSetsLoaded: (state, getters) => (module) => getters.filterModule(module).userFilterSetsLoaded,
  },
};
